<template>
  <div>

    <!-- No employee profile guard -->
    <div v-if="!employeeId" class="no-profile-box">
      <font-awesome-icon icon="user-slash" class="no-profile-icon" />
      <p class="no-profile-title">No employee profile linked</p>
      <p class="no-profile-sub">Your account is not linked to an employee record. Contact your HR administrator.</p>
    </div>

    <template v-else>

      <!-- Top: clock widget + today's shift -->
      <div class="top-grid">
        <clock-widget :employee-id="employeeId" />

        <!-- Today's shift card -->
        <div class="today-shift-card">
          <div class="tsc-header">
            <font-awesome-icon icon="calendar-day" class="tsc-icon" />
            <p class="tsc-title">Today's Shift</p>
          </div>
          <div v-if="todayShiftLoading" class="tsc-skeleton"></div>
          <div v-else-if="todayShift" class="tsc-body">
            <p class="tsc-name">{{ todayShift.shift?.name }}</p>
            <div class="tsc-times">
              <div class="tsc-time-block">
                <span class="tsc-label">Start</span>
                <span class="tsc-val">{{ fmtShiftTime(effectiveTimes(todayShift).start) }}</span>
              </div>
              <span class="tsc-sep">—</span>
              <div class="tsc-time-block">
                <span class="tsc-label">End</span>
                <span class="tsc-val">{{ fmtShiftTime(effectiveTimes(todayShift).end) }}</span>
              </div>
              <div v-if="!todayShift.rota_entry?.start_time && todayShift.shift?.break_minutes" class="tsc-time-block">
                <span class="tsc-label">Break</span>
                <span class="tsc-val">{{ todayShift.shift.break_minutes }}m</span>
              </div>
            </div>
            <div v-if="todayShift.shift?.client" class="tsc-client">
              <font-awesome-icon icon="building" />
              {{ todayShift.shift.client.customer_name }}
            </div>
            <div class="tsc-dates">
              {{ fmtDate(todayShift.date_from) }}
              <template v-if="todayShift.date_to && todayShift.date_to !== todayShift.date_from"> – {{ fmtDate(todayShift.date_to) }}</template>
              <template v-else-if="!todayShift.date_to"> (ongoing)</template>
            </div>
          </div>
          <div v-else class="tsc-empty">
            <font-awesome-icon icon="moon" />
            No shift assigned for today
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="tab-btn"
          :class="{ 'tab-btn--active': activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          <font-awesome-icon :icon="tab.icon" />
          {{ tab.label }}
        </button>
      </div>

      <!-- ══ Tab: Attendance History ══ -->
      <div v-if="activeTab === 'history'">
        <div class="filter-bar">
          <input type="date" class="filter-input" v-model="histFilters.date_from" />
          <input type="date" class="filter-input" v-model="histFilters.date_to" />
          <select class="filter-select" v-model="histFilters.status">
            <option value="">All Statuses</option>
            <option value="present">Present</option>
            <option value="absent">Absent</option>
            <option value="late">Late</option>
            <option value="early_departure">Early Departure</option>
            <option value="half_day">Half Day</option>
            <option value="on_leave">On Leave</option>
            <option value="holiday">Holiday</option>
          </select>
          <button class="btn-primary" @click="fetchHistory">
            <font-awesome-icon icon="search" /> Apply
          </button>
        </div>

        <!-- Summary chips -->
        <div v-if="summary" class="summary-strip">
          <div class="sum-chip sum-chip--green">
            <span class="sum-num">{{ summary.present || 0 }}</span>
            <span class="sum-lbl">Present</span>
          </div>
          <div class="sum-chip sum-chip--amber">
            <span class="sum-num">{{ summary.late || 0 }}</span>
            <span class="sum-lbl">Late</span>
          </div>
          <div class="sum-chip sum-chip--red">
            <span class="sum-num">{{ summary.absent || 0 }}</span>
            <span class="sum-lbl">Absent</span>
          </div>
          <div class="sum-chip sum-chip--orange">
            <span class="sum-num">{{ summary.early_departure || 0 }}</span>
            <span class="sum-lbl">Early Out</span>
          </div>
          <div class="sum-chip sum-chip--blue">
            <span class="sum-num">{{ summary.on_leave || 0 }}</span>
            <span class="sum-lbl">On Leave</span>
          </div>
          <div class="sum-chip sum-chip--gray">
            <span class="sum-num">{{ summary.holiday || 0 }}</span>
            <span class="sum-lbl">Holiday</span>
          </div>
        </div>

        <!-- Skeleton -->
        <div v-if="histLoading" class="sk-table">
          <div class="sk-row-head"></div>
          <div v-for="n in 8" :key="n" class="sk-row"></div>
        </div>

        <div v-else class="table-card">
          <table class="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Clock In</th>
                <th>Clock Out</th>
                <th>Hours</th>
                <th>Overtime</th>
                <th>Status</th>
                <th>Location Note</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in histLogs" :key="log.id">
                <td class="td-date">{{ fmtDate(log.work_date) }}</td>
                <td>{{ fmtTime(log.clock_in) || '—' }}</td>
                <td>{{ fmtTime(log.clock_out) || '—' }}</td>
                <td>{{ log.worked_hours != null ? log.worked_hours + 'h' : '—' }}</td>
                <td>{{ log.overtime_hours > 0 ? log.overtime_hours + 'h' : '—' }}</td>
                <td><span class="status-chip" :class="statusClass(log.status)">{{ statusLabel(log.status) }}</span></td>
                <td>
                  <span v-if="log.is_outside_location" class="loc-warn" :title="log.location_bypass_reason">
                    <font-awesome-icon icon="exclamation-triangle" /> Outside location
                  </span>
                  <span v-else class="td-muted">—</span>
                </td>
              </tr>
              <tr v-if="!histLogs.length">
                <td colspan="7" class="empty-cell">No attendance records found</td>
              </tr>
            </tbody>
          </table>
          <div class="pagination" v-if="histMeta.last_page > 1">
            <button class="page-btn" :disabled="histMeta.current_page === 1" @click="goHistPage(histMeta.current_page - 1)">‹</button>
            <span class="page-info">{{ histMeta.current_page }} / {{ histMeta.last_page }}</span>
            <button class="page-btn" :disabled="histMeta.current_page === histMeta.last_page" @click="goHistPage(histMeta.current_page + 1)">›</button>
          </div>
        </div>
      </div>

      <!-- ══ Tab: My Shifts ══ -->
      <div v-if="activeTab === 'shifts'">
        <div v-if="shiftsLoading" class="sk-table">
          <div class="sk-row-head"></div>
          <div v-for="n in 5" :key="n" class="sk-row"></div>
        </div>

        <div v-else class="table-card">
          <table class="data-table">
            <thead>
              <tr>
                <th>Shift</th>
                <th>Time</th>
                <th>Break</th>
                <th>Client</th>
                <th>Shift Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in myShifts" :key="a.id">
                <td>
                  <p class="shift-name">{{ a.shift?.name }}</p>
                </td>
                <td class="td-time">
                  {{ fmtShiftTime(effectiveTimes(a).start) }} – {{ fmtShiftTime(effectiveTimes(a).end) }}
                </td>
                <td class="td-muted">
                  {{ !a.rota_entry?.start_time && a.shift?.break_minutes ? a.shift.break_minutes + 'm' : '—' }}
                </td>
                <td>
                  <span v-if="a.shift?.client" class="shift-client">
                    <font-awesome-icon icon="building" style="font-size:10px;margin-right:4px;color:#7a90b0;" />
                    {{ a.shift.client.customer_name }}
                  </span>
                  <span v-else class="td-muted">—</span>
                </td>
                <td class="td-date">
                  <template v-if="a.date_from === a.date_to || !a.date_to">
                    {{ fmtDate(a.date_from) }}
                    <span v-if="!a.date_to" class="td-muted"> (Ongoing)</span>
                  </template>
                  <template v-else>
                    {{ fmtDate(a.date_from) }} – {{ fmtDate(a.date_to) }}
                  </template>
                </td>
                <td>
                  <span class="status-chip" :class="shiftStatusClass(a)">
                    {{ shiftStatusLabel(a) }}
                  </span>
                </td>
              </tr>
              <tr v-if="!myShifts.length">
                <td colspan="6" class="empty-cell">No shift assignments found</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { useStore } from 'vuex';
import ClockWidget from './clock-widget.vue';

export default {
  name: 'MyAttendancePage',
  components: { ClockWidget },
  data() {
    return {
      store: useStore(),
      activeTab: 'history',
      tabs: [
        { key: 'history', label: 'Attendance History', icon: 'history'    },
        { key: 'shifts',  label: 'My Shifts',          icon: 'calendar-alt' },
      ],

      // Today's shift
      todayShiftLoading: false,
      todayShift: null,

      // Attendance history
      histLoading: false,
      histLogs: [],
      histMeta: { current_page: 1, last_page: 1 },
      histFilters: {
        date_from: '',
        date_to:   '',
        status:    '',
        length:    20,
        page:      1,
      },
      summary: null,

      // My shifts
      shiftsLoading: false,
      myShifts: [],
    };
  },
  computed: {
    employeeId() {
      return this.store.state.getLoggedInUser?.employee_id || null;
    },
  },
  mounted() {
    if (!this.employeeId) return;
    const today = new Date().toISOString().slice(0, 10);
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
    this.histFilters.date_from = monthStart;
    this.histFilters.date_to   = today;
    this.fetchHistory();
    this.fetchMyShifts();
    this.fetchTodayShift();
  },
  methods: {
    async fetchTodayShift() {
      this.todayShiftLoading = true;
      const today = new Date().toISOString().slice(0, 10);
      try {
        const { data } = await axios.get('/hr/self-service/shifts');
        const list = data.data?.data || [];
        this.todayShift = list.find(a =>
          a.is_active &&
          a.date_from <= today &&
          (!a.date_to || a.date_to >= today)
        ) || null;
      } catch { /* silent — today's shift is non-critical */ }
      finally { this.todayShiftLoading = false; }
    },

    async fetchHistory() {
      this.histLoading = true;
      try {
        const { data } = await axios.get('/hr/self-service/attendance', {
          params: { ...this.histFilters },
        });
        this.histLogs = data.data?.data || [];
        this.histMeta = {
          current_page: data.data?.current_page || 1,
          last_page:    data.data?.last_page    || 1,
        };
        this.computeSummary();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load history');
      } finally { this.histLoading = false; }
    },

    computeSummary() {
      const s = { present: 0, late: 0, absent: 0, early_departure: 0, on_leave: 0, holiday: 0 };
      this.histLogs.forEach(l => { if (s[l.status] !== undefined) s[l.status]++; });
      this.summary = s;
    },

    goHistPage(p) { this.histFilters.page = p; this.fetchHistory(); },

    async fetchMyShifts() {
      this.shiftsLoading = true;
      try {
        const { data } = await axios.get('/hr/self-service/shifts');
        this.myShifts = data.data?.data || [];
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load shifts');
      } finally { this.shiftsLoading = false; }
    },

    effectiveTimes(a) {
      return {
        start: a?.rota_entry?.start_time || a?.start_time || a?.shift?.start_time || null,
        end:   a?.rota_entry?.end_time   || a?.end_time   || a?.shift?.end_time   || null,
      };
    },

    fmtShiftTime(t) {
      if (!t) return '—';
      const [h, m] = t.split(':').map(Number);
      const period = h >= 12 ? 'PM' : 'AM';
      const h12    = h % 12 || 12;
      return `${h12}:${String(m).padStart(2, '0')} ${period}`;
    },

    shiftStatusLabel(a) {
      const today = new Date().toISOString().slice(0, 10);
      if (a.date_to && a.date_to < today) return 'Ended';
      return 'Active';
    },

    shiftStatusClass(a) {
      const today = new Date().toISOString().slice(0, 10);
      if (a.date_to && a.date_to < today) return 'chip--gray';
      return 'chip--green';
    },

    fmtDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    fmtTime(dt) {
      if (!dt) return '';
      return new Date(dt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    },
    statusLabel(s) {
      return ({ present: 'Present', late: 'Late', absent: 'Absent', early_departure: 'Early Out',
                half_day: 'Half Day', on_leave: 'On Leave', holiday: 'Holiday' })[s] || s;
    },
    statusClass(s) {
      return ({ present: 'chip--green', late: 'chip--amber', absent: 'chip--red',
                early_departure: 'chip--orange', half_day: 'chip--purple',
                on_leave: 'chip--blue', holiday: 'chip--gray' })[s] || 'chip--gray';
    },
  },
};
</script>

<style scoped>
/* ── No-profile guard ───────────────────────────────────────────── */
.no-profile-box  { display:flex; flex-direction:column; align-items:center; padding:80px 20px; gap:16px; }
.no-profile-icon { font-size:48px; color:#dde5f0; }
.no-profile-title{ font-size:18px; font-weight:700; color:#1a2b45; margin:0; }
.no-profile-sub  { font-size:13px; color:#7a90b0; margin:0; text-align:center; max-width:360px; }

/* ── Top grid ────────────────────────────────────────────────────── */
.top-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:20px; }
@media (max-width:768px) { .top-grid { grid-template-columns:1fr; } }

/* ── Today's shift card ──────────────────────────────────────────── */
.today-shift-card { background:#fff; border:1px solid #e4eaf2; border-radius:16px; padding:20px; display:flex; flex-direction:column; gap:14px; }
.tsc-header  { display:flex; align-items:center; gap:10px; }
.tsc-icon    { color:#004AAD; font-size:18px; }
.tsc-title   { font-size:14px; font-weight:700; color:#1a2b45; margin:0; }
.tsc-skeleton{ height:120px; border-radius:10px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
.tsc-name    { font-size:18px; font-weight:800; color:#1a2b45; margin:0 0 12px; }
.tsc-times   { display:flex; align-items:center; gap:10px; background:#f8fafc; border-radius:10px; padding:12px 14px; }
.tsc-time-block { display:flex; flex-direction:column; gap:2px; flex:1; }
.tsc-label   { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; }
.tsc-val     { font-size:16px; font-weight:800; color:#1a2b45; }
.tsc-sep     { color:#aab8cc; font-size:16px; }
.tsc-client  { display:flex; align-items:center; gap:6px; font-size:12px; color:#4a5e78; background:#f0f7ff; border-radius:6px; padding:5px 10px; }
.tsc-dates   { font-size:11px; color:#7a90b0; }
.tsc-empty   { display:flex; align-items:center; justify-content:center; flex-direction:column; gap:10px; color:#aab8cc; font-size:13px; flex:1; min-height:80px; }
.tsc-empty svg { font-size:28px; opacity:.4; }

/* ── Tabs ────────────────────────────────────────────────────────── */
.tabs    { display:flex; gap:4px; border-bottom:2px solid #f0f4f8; margin-bottom:18px; }
.tab-btn { display:inline-flex; align-items:center; gap:7px; height:40px; padding:0 18px; background:none; border:none; border-bottom:2px solid transparent; margin-bottom:-2px; font-size:13px; font-weight:600; color:#7a90b0; cursor:pointer; transition:all .15s; }
.tab-btn:hover          { color:#004AAD; }
.tab-btn--active        { color:#004AAD; border-bottom-color:#004AAD; }

/* ── Filter bar ──────────────────────────────────────────────────── */
.filter-bar   { display:flex; align-items:center; flex-wrap:wrap; gap:8px; margin-bottom:14px; }
.filter-input,.filter-select { height:38px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; }
.btn-primary  { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary:hover { background:#003b8e; }

/* ── Summary strip ───────────────────────────────────────────────── */
.summary-strip { display:flex; flex-wrap:wrap; gap:10px; margin-bottom:14px; }
.sum-chip      { display:flex; flex-direction:column; align-items:center; gap:2px; padding:10px 18px; border-radius:10px; min-width:72px; }
.sum-num       { font-size:20px; font-weight:800; line-height:1; }
.sum-lbl       { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; opacity:.7; }
.sum-chip--green  { background:#dcfce7; color:#16a34a; }
.sum-chip--amber  { background:#fef9c3; color:#ca8a04; }
.sum-chip--red    { background:#fee2e2; color:#dc2626; }
.sum-chip--orange { background:#ffedd5; color:#ea580c; }
.sum-chip--blue   { background:#dbeafe; color:#1d4ed8; }
.sum-chip--gray   { background:#f1f5f9; color:#64748b; }

/* ── Table ───────────────────────────────────────────────────────── */
.table-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.data-table  { width:100%; border-collapse:collapse; font-size:13px; }
.data-table thead th { padding:12px 14px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .1s; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 14px; color:#1a2b45; vertical-align:middle; }
.empty-cell  { text-align:center; color:#aab8cc; padding:40px !important; }

.td-date    { font-size:13px; font-weight:600; white-space:nowrap; }
.td-time    { font-size:13px; font-weight:600; white-space:nowrap; color:#1a2b45; }
.td-muted   { color:#7a90b0; font-size:12px; }
.shift-name { font-size:13px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.shift-client { font-size:12px; color:#4a5e78; }

.status-chip  { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--amber  { background:#fef9c3; color:#ca8a04; }
.chip--red    { background:#fee2e2; color:#dc2626; }
.chip--orange { background:#ffedd5; color:#ea580c; }
.chip--purple { background:#f3e8ff; color:#7c3aed; }
.chip--blue   { background:#dbeafe; color:#1d4ed8; }
.chip--gray   { background:#f1f5f9; color:#64748b; }

.loc-warn { color:#ea580c; font-size:11px; font-weight:600; }

/* ── Pagination ──────────────────────────────────────────────────── */
.pagination { display:flex; align-items:center; justify-content:center; gap:12px; padding:14px; border-top:1px solid #f0f4f8; }
.page-btn   { background:#fff; border:1px solid #dde5f0; border-radius:6px; padding:4px 12px; font-size:15px; cursor:pointer; color:#1a2b45; }
.page-btn:disabled { opacity:.4; cursor:not-allowed; }
.page-info  { font-size:13px; color:#7a90b0; }

/* ── Skeleton ────────────────────────────────────────────────────── */
.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row-head,.sk-row { height:44px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; margin-bottom:1px; }
.sk-row-head { height:40px; background:#f8fafc; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
</style>
